import { Button, IconButton } from '@mui/material'
import {
  usePickerAdapter,
  usePickerTranslations,
} from '@mui/x-date-pickers/hooks'
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers/icons'
import type { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader'
import {
  useNextMonthDisabled,
  usePreviousMonthDisabled,
} from '@mui/x-date-pickers/internals'

const labelSx = {
  color: 'inherit',
  fontWeight: 500,
  fontSize: '1rem',
  minWidth: 0,
  px: 0.75,
  '&[aria-pressed="true"]': { textDecoration: 'underline' },
}

/**
 * One arrow in each corner, month and year between them. The month and the
 * year each open their own list, and the open one closes back to the days.
 * The calendar's RTL theme puts the previous month on the right.
 */
export default function CalendarHeader({
  currentMonth,
  views,
  view,
  onViewChange,
  onMonthChange,
  disabled,
  disableFuture,
  disablePast,
  minDate,
  maxDate,
  timezone,
  className,
}: PickersCalendarHeaderProps) {
  const adapter = usePickerAdapter()
  const translations = usePickerTranslations()
  const previousDisabled = usePreviousMonthDisabled(currentMonth, {
    disablePast,
    minDate,
    timezone,
  })
  const nextDisabled = useNextMonthDisabled(currentMonth, {
    disableFuture,
    maxDate,
    timezone,
  })
  const toggle = (target: 'month' | 'year') =>
    onViewChange?.(view === target ? 'day' : target)
  const arrowsHidden = view !== 'day'

  return (
    <div
      className={`MuiPickersCalendarHeader-root ${className ?? ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 8px 4px',
        minHeight: 40,
        flexShrink: 0,
      }}
    >
      <IconButton
        className="MuiPickersArrowSwitcher-button"
        size="small"
        aria-label={translations.previousMonth}
        title={translations.previousMonth}
        disabled={previousDisabled || arrowsHidden}
        style={arrowsHidden ? { visibility: 'hidden' } : undefined}
        onClick={() => onMonthChange(adapter.addMonths(currentMonth, -1))}
      >
        <ArrowRightIcon />
      </IconButton>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {views.includes('month') && (
          <Button
            className="MuiPickersCalendarHeader-label"
            sx={labelSx}
            disabled={disabled}
            aria-pressed={view === 'month'}
            onClick={() => toggle('month')}
          >
            {adapter.format(currentMonth, 'month')}
          </Button>
        )}
        {views.includes('year') && (
          <Button
            className="MuiPickersCalendarHeader-label"
            sx={labelSx}
            disabled={disabled}
            aria-pressed={view === 'year'}
            onClick={() => toggle('year')}
          >
            {adapter.format(currentMonth, 'year')}
          </Button>
        )}
      </div>
      <IconButton
        className="MuiPickersArrowSwitcher-button"
        size="small"
        aria-label={translations.nextMonth}
        title={translations.nextMonth}
        disabled={nextDisabled || arrowsHidden}
        style={arrowsHidden ? { visibility: 'hidden' } : undefined}
        onClick={() => onMonthChange(adapter.addMonths(currentMonth, 1))}
      >
        <ArrowLeftIcon />
      </IconButton>
    </div>
  )
}
